'use client';

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormHelperText,
  Link as FreddyLink,
  Loader,
  Select,
  Typography
} from '@coolblue-development/freddy';
import confirmApplicationTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/confirmApplicationTrigger';
import confirmPrivacyStatementTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/confirmPrivacyStatementTrigger';
import selectOpenPrivacyStatementTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectOpenPrivacyStatementTrigger';
import sendApplicationTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/sendApplicationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { useTranslation } from '@i18n/useTranslation';
import { applyForVacancy } from '@server/careers-backend/applyForVacancy';
import { ApplicationFormBlock } from '@sharedTypes';
import { getGoogleAnalyticsClientId } from '@utils/google-analytics/getGoogleAnalyticsClientId';
import { useClientUrl } from '@utils/url-generators/useClientUrl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { FailureMessage } from './FailureMessage';
import type { FormValues } from './FormValues';
import { InputFields } from './InputFields';
import { RejectionMessage } from './RejectionMessage';
import { SuccessMessage } from './SuccessMessage';

export const applicationFormSelector = 'applicationForm';

export const Form = ({
  applicationForm,
  setWithContainerStyles
}: {
  applicationForm: ApplicationFormBlock;
  setWithContainerStyles: (value: boolean) => void;
}) => {
  const { inputFields } = applicationForm;

  const { t, Trans } = useTranslation('vacancyDetail.applicationForm');

  const clientLogger = useClientLogger();
  const params = useParams();

  const privacyStatementHref = useClientUrl('privacy')!;
  const [privacyStatementArray, setPrivacyStatementArray] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [rejectionReasons, setRejectionReasons] = useState<string[] | null>(null);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const { trackEvent } = useAnalytics();

  // We don't need withContainer styles in PageRow when the success message is shown according to the design
  // Also will be used for the error message
  useEffect(() => {
    if (showSuccessMessage || rejectionReasons) {
      setWithContainerStyles(false);
    }
  }, [setWithContainerStyles, showSuccessMessage, rejectionReasons]);

  const methods = useForm<FormValues>({
    mode: 'onBlur',
    criteriaMode: 'all',
    reValidateMode: 'onBlur'
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors }
  } = methods;

  const onSubmit = async (input: FormValues) => {
    // Todo: Use the vacancyId from the location question when it is a vacancyGroupDetailPage
    const vacancyId =
      'vacancyId' in applicationForm ? (applicationForm.vacancyId as string) : input.vacancyId;
    if (!vacancyId) {
      setShowErrorMessage(true);
      clientLogger.error(
        'No vacancyId found',
        new Error(`VacancyId was not found for vacancy with slug: ${params.slug}`)
      );
      return;
    }

    setIsLoading(true);

    const candidateTrackingId = uuidv4();
    clientLogger.info('Submitting application form', {
      vacancyId,
      candidateTrackingId
    });

    const gaClientId = await getGoogleAnalyticsClientId();

    let applicationResult;
    try {
      applicationResult = await applyForVacancy(vacancyId, input, gaClientId, candidateTrackingId);

      if (applicationResult.success) {
        setShowSuccessMessage(true);
      } else if (!applicationResult.success && applicationResult.rejectionReasons) {
        setRejectionReasons(applicationResult.rejectionReasons);
      } else {
        setShowErrorMessage(true);
      }
    } catch (error) {
      clientLogger.error('Failed to submit application form', error as Error);
      setShowErrorMessage(true);
    }

    setIsLoading(false);

    const applyStatus = applicationResult?.success ? 'success' : 'failure';
    trackEvent(
      confirmApplicationTrigger({
        status: window.navigator.onLine ? applyStatus : 'no_delivery'
      })
    );
    trackEvent(sendApplicationTrigger({ unique_id: candidateTrackingId }));
  };

  const hasLocationOptions =
    'locationOptions' in applicationForm && !('vacancyId' in applicationForm);

  return (
    <FormProvider {...methods}>
      {isLoading && (
        <Box sx={{ position: 'relative', height: '20rem' }}>
          <Loader withBackdrop={false} />
        </Box>
      )}
      {!isLoading && showErrorMessage && (
        <FailureMessage title={t('failureMessage.title')} text={t('failureMessage.text')} />
      )}
      {!isLoading && !showErrorMessage && !showSuccessMessage && !rejectionReasons && (
        <Box
          component='form'
          onSubmit={handleSubmit(input => onSubmit(input))}
          id={applicationFormSelector}
        >
          <Typography variant='h2' mb={200} textAlign={{ md: 'left' }}>
            {t('title')}
          </Typography>

          <InputFields inputFields={inputFields} isLoading={isLoading}>
            {hasLocationOptions && (
              <Select
                label={t('questions.chooseLocation.question')}
                name='vacancyId'
                onChange={event => setValue('vacancyId', event.currentTarget.value)}
              >
                <option value=''>{t('questions.chooseLocation.placeholder')}</option>
                {applicationForm.locationOptions.map(locationOption => (
                  <option value={locationOption.vacancyId}>{locationOption.name}</option>
                ))}
              </Select>
            )}
          </InputFields>

          <Flex direction='column' gap={300} mt={{ xs: 500, md: 800 }}>
            <CheckboxGroup
              {...register('privacyStatement' as keyof FormValues, {
                required: t('questions.privacyStatement.rules.required.message')
              })}
              name='privacyStatement'
              value={privacyStatementArray}
              onChange={value => {
                setValue('privacyStatement', !getValues().privacyStatement);
                setPrivacyStatementArray(value);
                trackEvent(
                  confirmPrivacyStatementTrigger({
                    input_field: t('questions.privacyStatement.label'),
                    status: getValues().privacyStatement ? 'checked' : 'unchecked',
                    content: t('questions.privacyStatement.rules.required.message')
                  })
                );
              }}
              disabled={isLoading}
            >
              <Checkbox
                label={t('questions.privacyStatement.label')}
                name='privacyStatement.options.true'
                value='true'
              />
              {errors.privacyStatement?.message && (
                <FormHelperText validation='error'>
                  {errors.privacyStatement.message}
                </FormHelperText>
              )}
            </CheckboxGroup>

            <Typography margin={0} variant='caption'>
              <Trans
                t={t}
                i18nKey='questions.privacyStatement.readMoreAbout'
                components={{
                  privacyLink: (
                    <FreddyLink
                      component={Link}
                      href={privacyStatementHref}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={() => trackEvent(selectOpenPrivacyStatementTrigger())}
                    />
                  )
                }}
              />
            </Typography>

            <div>
              <Button variant='action' fullWidthUntil='md' type='submit' disabled={isLoading}>
                {t('submitButton.label')}
              </Button>
            </div>
          </Flex>
        </Box>
      )}
      {showSuccessMessage && <SuccessMessage />}
      {rejectionReasons && <RejectionMessage reasons={rejectionReasons} />}
    </FormProvider>
  );
};
